import { useInternationalization } from '@progress/kendo-react-intl'
import {
  type QueryFunctionContext,
  useInfiniteQuery,
} from '@tanstack/react-query'
import * as R from 'remeda'

import { graphql } from '~/gql'
import { useGraphQLRequest } from '~/hooks/useGraphQLRequest'
import { addProp } from '~/utils'

import { useStrategyMemosInfiniteQueryKey } from './useStrategyMemosInfiniteQueryKey'

const document = graphql(`
  query StrategyMemoListPage_StrategyMemos(
    $brandAccountId: ID!
    $offset: Int!
  ) {
    customerStrategyMemos(
      brandAccountId: $brandAccountId
      status: active
      pagination: { limit: 20, offset: $offset }
    ) {
      id
      title
      writer
      updatedAt
    }
  }
`)

type QueryKey = ReturnType<typeof useStrategyMemosInfiniteQueryKey>
type PageParam = number

export const useStrategyMemosInfiniteQuery = () => {
  const queryKey = useStrategyMemosInfiniteQueryKey()
  const graphQLRequest = useGraphQLRequest()
  const intl = useInternationalization()

  return useInfiniteQuery({
    queryFn: async ({
      pageParam: offset,
      queryKey: [_, variables],
    }: QueryFunctionContext<QueryKey, PageParam>) => {
      const result = await graphQLRequest({
        document,
        variables: { ...variables, offset },
      })

      return R.pipe(
        result.customerStrategyMemos ?? [],
        R.map(
          addProp('updatedAtText', (strategyMemo) =>
            intl.formatDate(
              new Date(strategyMemo.updatedAt),
              'yyyy/MM/dd HH:mm',
            ),
          ),
        ),
      )
    },
    queryKey,
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages): PageParam | undefined =>
      0 < lastPage.length ? allPages.flat().length : undefined,
  })
}

export type StrategyMemo = QueryResultOf<
  typeof useStrategyMemosInfiniteQuery
>[number]
